import { useMemo } from 'react';
import { useJobs } from './useJobs';
import { useInstaller } from '../contexts/InstallerContext';
import { justDate, isoDateString, addDays } from '../utils/date-utils';

export function useInstallerJobs() {
  const { jobs } = useJobs();
  const installer = useInstaller();

  const installerJobs = useMemo(() => {
    if (!installer.crewId) return [];

    const filtered = jobs.filter(job => {
      // Filter out jobs without scheduled dates
      if (!job.scheduledDate) return false;
      
      // Filter out completed, cancelled, and ON HOLD jobs
      if (job.status === 'completed' || job.status === 'cancelled' || job.status === 'on_hold') return false;
      
      // Filter out jobs without assigned crew
      if (!job.assignedCrew) return false;

      // Check if this installer's crew matches the job's assigned crew
      const crewId = job.assignedCrew.toLowerCase();
      const installerCrewId = installer.crewId.toLowerCase();

      // Direct match
      if (crewId === installerCrewId) return true;

      // Check if installer is part of a multi-person crew (e.g., "adam-kevin")
      if (crewId.includes('-')) {
        const crewMembers = crewId.split('-');
        return crewMembers.includes(installerCrewId);
      }

      return false;
    });

    return filtered.sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate));
  }, [jobs, installer.crewId]);

  return installerJobs;
}

export function groupJobsByDate(jobs) {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  
  const todayDate = justDate(now);
  const todayISO = isoDateString(todayDate);
  
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowDate = justDate(tomorrow);
  const tomorrowISO = isoDateString(tomorrowDate);
  
  const currentDayOfWeek = now.getDay();
  const daysUntilSunday = currentDayOfWeek === 0 ? 7 : 7 - currentDayOfWeek;
  const endOfThisWeek = new Date(now);
  endOfThisWeek.setDate(endOfThisWeek.getDate() + daysUntilSunday);
  const endOfWeekDate = justDate(endOfThisWeek);

  const jobsByDate = {};
  const overdueJobs = [];

  jobs.forEach(job => {
    const startDate = justDate(new Date(job.scheduledDate));
    const duration = job.durationDays || 1;

    for (let i = 0; i < duration; i++) {
      const currentDate = addDays(startDate, i);
      const dateISO = isoDateString(currentDate);
      
      // Check if this date is in the past (overdue)
      if (currentDate < todayDate) {
        // Add to overdue list (only once per job)
        if (i === 0) {
          overdueJobs.push({
            ...job,
            _displayDate: currentDate,
            _dayInfo: ''
          });
        }
        continue; // Skip adding to regular date groups
      }

      if (!jobsByDate[dateISO]) {
        jobsByDate[dateISO] = [];
      }
      
      const dayInfo = i === 0 ? '' : ` (Day ${i + 1} of ${duration})`;
      jobsByDate[dateISO].push({
        ...job,
        _displayDate: currentDate,
        _dayInfo: dayInfo
      });
    }
  });

  // Sort overdue jobs by scheduled date (oldest first)
  overdueJobs.sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate));

  const today = jobsByDate[todayISO] || [];
  const tomorrowJobs = jobsByDate[tomorrowISO] || [];
  const thisWeek = [];
  const upcoming = [];

  Object.keys(jobsByDate).forEach(dateISO => {
    const date = new Date(dateISO + 'T00:00:00');
    const jobDate = justDate(date);

    if (dateISO !== todayISO && dateISO !== tomorrowISO) {
      if (jobDate > tomorrowDate && jobDate <= endOfWeekDate) {
        thisWeek.push(...jobsByDate[dateISO]);
      } else if (jobDate > endOfWeekDate) {
        upcoming.push(...jobsByDate[dateISO]);
      }
    }
  });

  thisWeek.sort((a, b) => new Date(a._displayDate) - new Date(b._displayDate));
  upcoming.sort((a, b) => new Date(a._displayDate) - new Date(b._displayDate));

  return {
    overdue: overdueJobs,
    today,
    tomorrow: tomorrowJobs,
    thisWeek,
    upcoming
  };
}

export function getCrewPartners(crewId, currentInstallerName) {
  if (!crewId || !crewId.includes('-')) return null;

  const crewMap = {
    'adam': 'Adam',
    'kevin': 'Kevin',
    'steve': 'Steve'
  };

  const members = crewId.toLowerCase().split('-').map(id => crewMap[id]).filter(Boolean);
  
  const partners = members.filter(name => name !== currentInstallerName);
  
  if (partners.length === 0) return null;
  
  return partners.join(' + ');
}